<template>
  <slot>
    <LibrarySummarySkeletonLoader
      v-if="isLoading"
      :number-of-metrics="numberOfMetrics"
    />
    <div
      v-else
      class="summaries-wrapper"
    >
      <p
        v-for="{ label, value, color } in summaries"
        :key="label"
        class="summary text-textDarkest"
      >
        {{ label }}
        <span :style="`color: ${label === 'Average Score' ? getColor(value) : color};`">
          {{ value }}
        </span>
      </p>
    </div>
  </slot>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useColors } from '@/composables/colors';
import { numberWithCommas, colorMap } from '@/helpers/formatters';
import LibrarySummarySkeletonLoader from '@/components/shared/SkeletonLoaders/LibrarySummarySkeletonLoader.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  totals: { type: Object, default() {} },
  documentType: { type: String, default: 'published' },
});

const route = useRoute();

const documentLibrarySummaryViews = [
  {
    key: 'average_score',
    label: 'Average Score',
  },
  {
    key: 'total_job_posts',
    label: 'Total Job Posts',
    color: '#4022A1',
  },
];

const requisitionLibrarySummaryViews = computed(() => ([
  {
    key: 'total_page_requisitions',
    label: `Total ${route.meta.title}`,
    color: '#4022A1',
  },
]));

const summaryViews = computed(() => {
  if (route.name.includes('requisitions')) return requisitionLibrarySummaryViews.value;
  return documentLibrarySummaryViews;
});

const numberOfMetrics = computed(() => summaryViews.value.length);

const summaries = computed(() => {
  const filteredSummaries = summaryViews.value.filter((summaryView) => {
    if (props.totals[summaryView.key] === null || props.totals[summaryView.key] === undefined) return false;
    return true;
  });

  return filteredSummaries.map((filteredSummary) => ({
    ...filteredSummary,
    value: numberWithCommas(props.totals[filteredSummary.key]),
  }));
});

const {
  brandPrimary,
  grey800,
  red700,
  orange700,
  blue700,
  green700,
} = useColors();
const colorsToMap = {
  grey800,
  red700,
  orange700,
  blue700,
  green700,
};

const getColor = (score) => {
  if (score === 0 || score === '0') {
    return brandPrimary;
  }
  return colorMap(score, colorsToMap);
};
</script>

<style scoped lang="less">
.summaries-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  margin-left: 12px;
}

.summary {
    font-size: 13px;
    font-weight: 500;
    line-height: 150%;
}
</style>
